import { Injectable } from '@angular/core';

export type ColumnType = 'DECIMAL' | 'INTEGER' | 'TEXT' | 'DATE' | 'BIGINT';

const SQL_RESERVED = new Set([
    'BINARY',
    'INTEGER',
    'FLOAT',
    'DECIMAL',
    'CHAR',
    'VARCHAR',
    'TEXT',
    'DATE',
    'TIME',
    'TIMESTAMP',
    'BOOLEAN',
    'BIGINT',
    'SMALLINT',
    'TINYINT',
    'MEDIUMINT',
    'DOUBLE',
    'REAL',
    'NUMERIC',
    'BLOB',
    'CLOB',
    'ENUM',
    'SET',
    'JSON',
    'XML',
    'SELECT',
    'INSERT',
    'UPDATE',
    'DELETE',
    'MERGE',
    'REPLACE',
    'DROP',
    'CREATE',
    'ALTER',
    'RENAME',
    'TRUNCATE',
    'GRANT',
    'REVOKE',
    'BEGIN',
    'COMMIT',
    'ROLLBACK',
    'SAVEPOINT',
    'SET',
    'DECLARE',
    'FETCH',
    'CLOSE',
    'DEALLOCATE',
    'EXECUTE',
    'OPEN',
    'USING',
    'ID',
    'WHERE',
    'ORDER',
    'GROUP',
    'BY',
    'HAVING',
    'JOIN',
    'INNER',
    'LEFT',
    'RIGHT',
    'FULL',
    'CROSS',
    'UNION',
    'INTERSECT',
    'EXCEPT',
    'LIMIT',
    'OFFSET',
    'FETCH',
    'DISTINCT',
    'AS',
    'IN',
    'EXISTS',
    'LIKE',
    'BETWEEN',
    'IS',
    'NULL',
    'NOT',
    'AND',
    'OR',
    'CASE',
    'WHEN',
    'THEN',
    'ELSE',
    'END',
    'PRIMARY',
    'KEY',
    'FOREIGN',
    'UNIQUE',
    'CHECK',
    'INDEX',
    'AUTO_INCREMENT',
    'CAST',
    'CONVERT',
    'CURRENT_DATE',
    'CURRENT_TIME',
    'CURRENT_TIMESTAMP',
    'CURRENT_USER',
    'SESSION_USER',
    'SYSTEM_USER',
    'USER',
    'IFNULL',
    'COALESCE',
    'NVL',
    'GREATEST',
    'LEAST',
    'TABLE',
    'VIEW',
    'SEQUENCE',
    'TRIGGER',
    'CURSOR',
    'PROCEDURE',
    'FUNCTION',
    'DATABASE',
    'SCHEMA',
    'COLUMN',
    'ROW',
    'CONSTRAINT',
    'REFERENCES',
    'ON',
    'TO',
    'FROM',
    'ALL',
    'ANY',
    'SOME',
    'WITH',
    'WITHOUT',
    'OVER',
    'PARTITION',
    'RANGE',
    'ROWS',
    'UNBOUNDED',
    'PRECEDING',
    'FOLLOWING',
    'CURRENT',
    'NEXT',
    'FIRST',
    'LAST',
    'WINDOW',
    'NULLS',
    'TRUE',
    'FALSE',
    'ESCAPE',
    'DEFAULT',
    'LOCALTIME',
    'LOCALTIMESTAMP',
    'ROWID',
    'SCOPE',
    'CONNECT',
    'INTERVAL',
    'OVERLAY',
    'POSITION',
    'START',
    'END',
    'THIS',
    'NEW',
]);

@Injectable({ providedIn: 'root' })
export class TableFileService {
    // ---------------- BASIC CLEANERS ----------------
    private static readonly EMPTY_MARKERS = new Set([
        '', 'null', 'nan', 'n/a', 'na', 'none', '#n/a', '#value!', '-',
    ]);

    cleanValue(val: any): any {
        if (val === null || val === undefined) return null;
        const s = String(val).trim();
        if (TableFileService.EMPTY_MARKERS.has(s.toLowerCase())) return null;
        return val;
    }

    cleanHeaderKey(h: string): string {
        return String(h)
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_+|_+$/g, '');
    }

    isReservedKeyword(h: string): boolean {
        return SQL_RESERVED.has(h.toUpperCase());
    }

    /** Checks the column name the backend will see (after cleaning), as the backend does. */
    isReservedHeader(h: string): boolean {
        return this.isReservedKeyword(this.cleanHeaderKey(h));
    }

    /**
     * New tables only. SAI adds its own `id` primary key and SQL keywords cannot be column
     * names, so a header that cleans to one of them is renamed to "Record <header>"
     * (`record_id`, `record_date`, ...), with " 2", " 3" when that name is already taken.
     * Other headers are returned unchanged.
     */
    renameReservedHeaders(headers: string[]): {
        headers: string[];
        renamed: { from: string; to: string }[];
    } {
        const taken = new Set(headers.map(h => this.cleanHeaderKey(h)));
        const renamed: { from: string; to: string }[] = [];
        const out = headers.map((h) => {
            if (!this.isReservedHeader(h)) {
                return h;
            }
            const base = `Record ${String(h).trim()}`;
            let next = base;
            for (let n = 2; taken.has(this.cleanHeaderKey(next)); n++) {
                next = `${base} ${n}`;
            }
            taken.add(this.cleanHeaderKey(next));
            renamed.push({ from: h, to: next });
            return next;
        });
        return { headers: out, renamed };
    }

    // ---------------- CSV ACCOUNTING FORMAT ----------------
    processCSVDataForAccountingFormat(rows: any[]): any[] {
        return rows.map((row) => {
            const processedRow = { ...row };

            for (const key in processedRow) {
                if (!Object.prototype.hasOwnProperty.call(processedRow, key)) continue;
                const value = processedRow[key];

                if (typeof value === 'string') {
                    const trimmedValue = value.trim();

                    // (5) -> -5  (only when the entire value is parentheses around a number)
                    if (trimmedValue.startsWith('(') && trimmedValue.endsWith(')')) {
                        const numberStr = trimmedValue.slice(1, -1).trim();
                        const numberValue = parseFloat(numberStr.replace(/,/g, ''));
                        if (!isNaN(numberValue) && this.NUMERIC_RE.test(numberStr.replace(/,/g, ''))) {
                            processedRow[key] = -numberValue;
                        }
                    }
                    // Weird parentheses but entire value is numeric (e.g. "1,234.56" or " ( 99 ) ")
                    // Do NOT convert text like "20 S PUFF PASTRY (9X800G" - only digits/parentheses/commas/dots
                    else if (/^[\s().,0-9+-eE]+$/.test(trimmedValue)) {
                        const cleanValue = trimmedValue.replace(/[()]/g, '').replace(/,/g, '').trim();
                        if (this.NUMERIC_RE.test(cleanValue)) {
                            const numberValue = parseFloat(cleanValue);
                            if (!isNaN(numberValue)) {
                                processedRow[key] = numberValue;
                            }
                        }
                    }
                }
            }

            return processedRow;
        });
    }

    // ---------------- TYPE INFERENCE ----------------
    private NUMERIC_RE = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;

    private isValidYMD(y: number, m: number, d: number): boolean {
        const dt = new Date(Date.UTC(y, m - 1, d));
        return (
            dt.getUTCFullYear() === y &&
            dt.getUTCMonth() === m - 1 &&
            dt.getUTCDate() === d
        );
    }

    private isDateString(s: string): boolean {
        const str = String(s).trim();

        // ISO-like: YYYY-MM-DD, YYYY/MM/DD, YYYY.MM.DD
        let m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(str);
        if (m) return this.isValidYMD(+m[1], +m[2], +m[3]);

        // D-M-Y or M-D-Y
        m = /^(\d{1,2})-(\d{1,2})-(\d{2,4})$/.exec(str);
        if (m) {
            const y = +m[3] < 100 ? +m[3] + 2000 : +m[3];
            if (this.isValidYMD(y, +m[2], +m[1])) return true;
            if (this.isValidYMD(y, +m[1], +m[2])) return true;
        }

        // D/M/Y or M/D/Y
        m = /^(\d{1,2})[/.](\d{1,2})[/.](\d{2,4})$/.exec(str);
        if (m) {
            const y = +m[3] < 100 ? +m[3] + 2000 : +m[3];
            if (this.isValidYMD(y, +m[2], +m[1])) return true;
            if (this.isValidYMD(y, +m[1], +m[2])) return true;
        }

        // 1 Jan 2024
        m = /^(\d{1,2})\s+([A-Za-z]+)\s+(\d{2,4})$/.exec(str);
        if (m) {
            const mon = m[2].slice(0, 3).toLowerCase();
            const monthMap: Record<string, number> = {
                jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
                jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
            };
            const mm = monthMap[mon];
            if (mm) {
                const y = +m[3] < 100 ? +m[3] + 2000 : +m[3];
                return this.isValidYMD(y, mm, +m[1]);
            }
        }

        return false;
    }

    detectTypeForColumn(sample: any[][], colIdx: number): ColumnType {
        let nonEmpty = 0;
        let dateHits = 0;
        let numericHits = 0;
        let decimalHits = 0;
        let maxAbsInteger = 0;
        let textEvidence = 0;

        for (let r = 0; r < sample.length; r++) {
            const v = sample[r][colIdx];
            if (v === null || v === undefined) continue;

            const raw = String(v).trim();
            if (TableFileService.EMPTY_MARKERS.has(raw.toLowerCase())) continue;
            if (/^-+$/.test(raw)) continue;

            nonEmpty++;

            if (v instanceof Date) {
                dateHits++;
                continue;
            }

            if (this.isDateString(raw)) {
                dateHits++;
                continue;
            }

            // Strip percentage sign and treat as decimal
            const isPercent = raw.endsWith('%');
            const cleaned = (isPercent ? raw.slice(0, -1).trim() : raw).replace(/,/g, '');

            if (this.NUMERIC_RE.test(cleaned)) {
                const n = Number(cleaned);
                if (Number.isFinite(n)) {
                    numericHits++;
                    if (isPercent || !Number.isInteger(n)) {
                        decimalHits++;
                    } else {
                        const abs = Math.abs(n);
                        if (abs > maxAbsInteger) maxAbsInteger = abs;
                    }
                    continue;
                }
            }

            textEvidence++;
        }

        if (nonEmpty === 0) return 'TEXT';

        // Even a single text value makes the entire column TEXT
        if (textEvidence > 0) return 'TEXT';

        // All non-empty values are dates
        if (dateHits > 0 && dateHits === nonEmpty) return 'DATE';

        // Any numeric presence (even 1 among nulls/zeros) determines the type
        if (numericHits > 0) {
            if (decimalHits > 0) return 'DECIMAL';
            return maxAbsInteger > 2147483647 ? 'BIGINT' : 'INTEGER';
        }

        return 'TEXT';
    }

    inferTypesFromSample(headers: string[], rows: any[][]): ColumnType[] {
        if (!rows.length || !headers.length) {
            return [];
        }
        return headers.map((_, idx) => this.detectTypeForColumn(rows, idx));
    }

    /** Same row extraction as initial upload (`transformTableContent`). */
    rowsFromCsvObjects(rowsObj: Record<string, unknown>[]): {
        headers: string[];
        rows: any[][];
    } {
        const headers = Object.keys(rowsObj[0] ?? {});
        const rows = rowsObj.map(o =>
            headers.map(h => this.cleanValue(o[h]))
        );
        return { headers, rows };
    }

    /** Same row extraction as initial upload (`transformTableContent`, xlsx). */
    rowsFromXlsxAoA(aoa: string[][]): { headers: string[]; rows: any[][] } {
        const headers = (aoa[0] || []).map(h => String(h).trim());
        const rows = aoa
            .slice(1)
            .map(r => headers.map((_, i) => this.cleanValue(r[i])));
        return { headers, rows };
    }

    resolveExpectedColumnTypes(
        originalHeaders: string[],
        originalHeadersClean: string[],
        originalTypes: ColumnType[],
        apiSchema: Record<string, string>
    ): ColumnType[] {
        const useApiSchema = Object.keys(apiSchema).length > 0;
        return originalHeaders.map((h, i) => {
            if (useApiSchema) {
                const clean = originalHeadersClean[i];
                const apiType = apiSchema[clean] || apiSchema[h];
                if (apiType) {
                    return this.normalizeApiTypeToColumnType(apiType);
                }
            }
            return originalTypes[i] ?? 'TEXT';
        });
    }

    /** Uses the same parsing rules as `detectTypeForColumn` for a single cell. */
    valueMatchesColumnType(value: unknown, expectedType: ColumnType): boolean {
        if (value === null || value === undefined) {
            return true;
        }

        const raw = String(value).trim();
        if (TableFileService.EMPTY_MARKERS.has(raw.toLowerCase())) {
            return true;
        }
        if (/^-+$/.test(raw)) {
            return true;
        }

        if (value instanceof Date) {
            return expectedType === 'DATE';
        }

        if (expectedType === 'DATE') {
            return this.isDateString(raw);
        }

        const isPercent = raw.endsWith('%');
        const cleaned = (isPercent ? raw.slice(0, -1).trim() : raw).replace(
            /,/g,
            ''
        );

        if (this.NUMERIC_RE.test(cleaned)) {
            const n = Number(cleaned);
            if (!Number.isFinite(n)) {
                return false;
            }

            switch (expectedType) {
                case 'DECIMAL':
                    return true;
                case 'INTEGER':
                    return (
                        !isPercent &&
                        Number.isInteger(n) &&
                        Math.abs(n) <= 2147483647
                    );
                case 'BIGINT':
                    return !isPercent && Number.isInteger(n);
                case 'TEXT':
                    return true;
                default:
                    return false;
            }
        }

        return expectedType === 'TEXT';
    }

    private validateRowValuesAgainstTypes(
        originalHeaders: string[],
        expectedTypes: ColumnType[],
        newRows: any[][]
    ): { ok: boolean; typesMsg: string } {
        const typeDiffs: string[] = [];

        for (let i = 0; i < originalHeaders.length; i++) {
            const expectedType = expectedTypes[i];
            const invalidRowIndex = newRows.findIndex(
                r => !this.valueMatchesColumnType(r[i], expectedType)
            );
            if (invalidRowIndex !== -1) {
                const invalidValue = newRows[invalidRowIndex][i];
                const displayValue =
                    invalidValue === null || invalidValue === undefined
                        ? '(empty)'
                        : String(invalidValue);
                typeDiffs.push(
                    `${originalHeaders[i]}: value "${displayValue}" is not compatible with ${expectedType}`
                );
            }
        }

        return {
            ok: typeDiffs.length === 0,
            typesMsg: typeDiffs.length ? typeDiffs.join(', ') : 'OK',
        };
    }

    normalizeApiTypeToColumnType(apiType: string): ColumnType {
        const upperType = apiType.toUpperCase();
        if (
            upperType === 'TEXT' ||
            upperType === 'VARCHAR' ||
            upperType === 'CHAR'
        )
            return 'TEXT';
        if (
            upperType === 'DECIMAL' ||
            upperType === 'NUMERIC' ||
            upperType === 'FLOAT' ||
            upperType === 'DOUBLE' ||
            upperType === 'REAL'
        )
            return 'DECIMAL';
        if (
            upperType === 'INTEGER' ||
            upperType === 'INT' ||
            upperType === 'SMALLINT' ||
            upperType === 'TINYINT' ||
            upperType === 'MEDIUMINT'
        )
            return 'INTEGER';
        if (upperType === 'BIGINT') return 'BIGINT';
        if (
            upperType === 'DATE' ||
            upperType === 'DATETIME' ||
            upperType === 'TIMESTAMP'
        )
            return 'DATE';
        return 'TEXT';
    }

    mapRowsToObjects(headers: string[], rows: any[][]): any[] {
        return rows.map(r =>
            headers.reduce((acc, h, i) => {
                acc[this.cleanHeaderKey(h)] = r[i];
                return acc;
            }, {} as Record<string, any>)
        );
    }

    mergeApiHeaderRecord(
        target: Record<string, string>,
        source: Record<string, unknown> | null | undefined
    ): void {
        if (!source || typeof source !== 'object') {
            return;
        }
        for (const [key, value] of Object.entries(source)) {
            if (value == null || value === '') {
                continue;
            }
            const strValue = String(value);
            const cleanKey = this.cleanHeaderKey(key);
            target[cleanKey] = strValue;
            if (cleanKey !== key.toLowerCase()) {
                target[key.toLowerCase()] = strValue;
            }
        }
    }

    resolveDateFormatFromComments(
        header: string,
        headerClean: string,
        apiComments: Record<string, string>
    ): string | undefined {
        const format =
            apiComments[headerClean] ||
            apiComments[header.toLowerCase()] ||
            apiComments[header];
        if (!format || format === 'No Unit' || format === 'default') {
            return undefined;
        }
        return format;
    }

    buildAppendDateColumns(params: {
        originalHeaders: string[];
        originalHeadersClean: string[];
        originalTypes: ColumnType[];
        apiSchema: Record<string, string>;
        apiComments: Record<string, string>;
    }): Record<string, string> {
        const {
            originalHeaders,
            originalHeadersClean,
            originalTypes,
            apiSchema,
            apiComments,
        } = params;
        const useApiSchema = Object.keys(apiSchema).length > 0;
        const dateColumns: Record<string, string> = {};

        for (let i = 0; i < originalHeaders.length; i++) {
            const origHeader = originalHeaders[i];
            const origHeaderClean = originalHeadersClean[i];
            const isDateColumn = useApiSchema
                ? (
                      apiSchema[origHeaderClean] || apiSchema[origHeader]
                  )?.toUpperCase() === 'DATE'
                : originalTypes[i] === 'DATE';

            if (!isDateColumn) {
                continue;
            }

            const format = this.resolveDateFormatFromComments(
                origHeader,
                origHeaderClean,
                apiComments
            );
            if (format) {
                dateColumns[origHeaderClean] = format;
            }
        }

        return dateColumns;
    }

    buildAppendComments(
        originalHeaders: string[],
        apiComments: Record<string, string>
    ): Record<string, string> {
        return Object.fromEntries(
            originalHeaders.map((h) => {
                const key = this.cleanHeaderKey(h);
                const comment =
                    apiComments[key] ||
                    apiComments[h.toLowerCase()] ||
                    apiComments[h] ||
                    'No Unit';
                return [key, comment];
            })
        );
    }

    // ---------------- APPEND VALIDATION ----------------
    private arraysEqual<T>(a: T[], b: T[]): boolean {
        return a.length === b.length && a.every((v, i) => v === b[i]);
    }

    /** True when both arrays hold the same column names (multiset), regardless of order. */
    private sameColumnSet(a: string[], b: string[]): boolean {
        if (a.length !== b.length) return false;
        const counts = new Map<string, number>();
        for (const name of a) counts.set(name, (counts.get(name) ?? 0) + 1);
        for (const name of b) {
            const remaining = counts.get(name);
            if (!remaining) return false;
            counts.set(name, remaining - 1);
        }
        return Array.from(counts.values()).every(n => n === 0);
    }

    /**
     * Human-readable column mismatch summary. Shows the *normalized* names actually
     * compared plus the exact set difference (missing / unexpected), so the real cause
     * (e.g. `period` vs `periods`) is obvious instead of buried in a raw header dump.
     */
    private buildColumnMismatchMessage(
        originalHeadersClean: string[],
        newHeaders: string[],
        newHeadersClean: string[]
    ): string {
        const expectedSet = new Set(originalHeadersClean);
        const gotSet = new Set(newHeadersClean);

        const rawByClean = new Map<string, string>();
        newHeadersClean.forEach((cleanName, i) => {
            if (!rawByClean.has(cleanName)) rawByClean.set(cleanName, newHeaders[i]);
        });

        const missing = originalHeadersClean.filter(h => !gotSet.has(h));
        const unexpected = newHeadersClean
            .filter(h => !expectedSet.has(h))
            .map((cleanName) => {
                const raw = rawByClean.get(cleanName);
                return raw && raw !== cleanName
                    ? `${cleanName} (from "${raw}")`
                    : cleanName;
            });

        const parts = [
            `Expected columns: [${originalHeadersClean.join(', ')}]`,
            `Found columns: [${newHeadersClean.join(', ')}]`,
        ];
        if (missing.length) parts.push(`Missing: ${missing.join(', ')}`);
        if (unexpected.length) parts.push(`Unexpected: ${unexpected.join(', ')}`);
        parts.push(
            'Names are compared after lowercasing and converting spaces/symbols to underscores; column order does not matter.'
        );
        return parts.join(' — ');
    }

    /**
     * Realigns each row so column `i` matches `originalHeadersClean[i]`, letting an append
     * succeed when the incoming file has the same columns in a different order. Duplicate
     * names are consumed left-to-right; an original column with no source yields null. Rows
     * are returned untouched when the headers are already in the original order.
     */
    private reorderRowsToOriginal(
        newHeadersClean: string[],
        originalHeadersClean: string[],
        rows: unknown[][]
    ): unknown[][] {
        if (this.arraysEqual(newHeadersClean, originalHeadersClean)) {
            return rows;
        }
        const sourcesByName = new Map<string, number[]>();
        newHeadersClean.forEach((name, idx) => {
            const list = sourcesByName.get(name);
            if (list) list.push(idx);
            else sourcesByName.set(name, [idx]);
        });
        const sourceForOriginal = originalHeadersClean.map((name) => {
            const list = sourcesByName.get(name);
            return list && list.length ? list.shift()! : -1;
        });
        return rows.map(row =>
            sourceForOriginal.map(idx => (idx === -1 ? null : row[idx]))
        );
    }

    // ---------------- DATE NORMALIZATION ----------------

    /** Splits a declared format like "DD-MM-YYYY" into its order token + separator. */
    private parseDeclaredDateFormat(fmt: string): { order: string; sep: string } {
        const order = fmt.toUpperCase().replace(/[^A-Z]/g, '');
        const sepMatch = fmt.match(/[^A-Za-z0-9]/);
        return { order, sep: sepMatch ? sepMatch[0] : '-' };
    }

    private formatYMD(
        y: number,
        m: number,
        d: number,
        order: string,
        sep: string
    ): string {
        const yyyy = String(y).padStart(4, '0');
        const mm = String(m).padStart(2, '0');
        const dd = String(d).padStart(2, '0');
        switch (order) {
            case 'MMDDYYYY':
                return [mm, dd, yyyy].join(sep);
            case 'YYYYMMDD':
                return [yyyy, mm, dd].join(sep);
            case 'YYYYDDMM':
                return [yyyy, dd, mm].join(sep);
            case 'DDMMYYYY':
            default:
                return [dd, mm, yyyy].join(sep);
        }
    }

    /**
     * Parses a date value flexibly and re-serializes it into `expectedFmt`. Accepts `-`,
     * `/`, `.` or whitespace separators and either year-first (ISO) or year-last input;
     * the declared format disambiguates day-vs-month order for year-last values. So
     * `2024-01-02`, `2024/01/02`, `02/01/2024` and `02-01-2024` all normalize to the same
     * date. Returns the value rewritten in the declared format, or null if it is not a
     * valid date.
     */
    private normalizeDateValue(value: unknown, expectedFmt: string): string | null {
        const { order, sep } = this.parseDeclaredDateFormat(expectedFmt);

        let y: number;
        let m: number;
        let d: number;

        if (value instanceof Date) {
            y = value.getUTCFullYear();
            m = value.getUTCMonth() + 1;
            d = value.getUTCDate();
        } else {
            const parts = String(value ?? '')
                .trim()
                .split(/[-/.\s]+/)
                .filter(Boolean);
            if (parts.length !== 3 || !parts.every(p => /^\d+$/.test(p))) {
                return null;
            }
            const nums = parts.map(Number);
            if (parts[0].length === 4) {
                // year-first (ISO) is always Y, M, D
                [y, m, d] = nums;
            } else {
                y = nums[2] < 100 ? nums[2] + 2000 : nums[2];
                const monthFirst =
                    order.startsWith('MM') || order === 'YYYYMMDD';
                if (monthFirst) {
                    m = nums[0];
                    d = nums[1];
                } else {
                    d = nums[0];
                    m = nums[1];
                }
            }
        }

        if (!this.isValidYMD(y, m, d)) {
            return null;
        }
        return this.formatYMD(y, m, d, order, sep);
    }

    /**
     * Normalizes every declared date column in `rows` to its expected format, returning the
     * rewritten rows. Reports the first cell that cannot be read as a date. Rows are copied
     * so the caller's source data is never mutated.
     */
    private normalizeDateColumnsForAppend(
        rows: unknown[][],
        dateFormatByColumn: (string | null)[],
        originalHeaders: string[]
    ): { rows: unknown[][]; error: string | null } {
        const out = rows.map(r => r.slice());
        for (let i = 0; i < dateFormatByColumn.length; i++) {
            const fmt = dateFormatByColumn[i];
            if (!fmt) continue;
            for (let r = 0; r < out.length; r++) {
                const cell = out[r][i];
                if (this.cleanValue(cell) == null) continue;
                const normalized = this.normalizeDateValue(cell, fmt);
                if (normalized === null) {
                    return {
                        rows: out,
                        error: `Date format mismatch at column "${originalHeaders[i]}". Could not read "${String(
                            cell
                        )}" as a date (expected a format like ${fmt}).`,
                    };
                }
                out[r][i] = normalized;
            }
        }
        return { rows: out, error: null };
    }

    validateAppendAgainstOriginal(params: {
        originalHeaders: string[];
        originalHeadersClean: string[];
        originalTypes: ColumnType[];
        apiSchema: Record<string, string>;
        apiComments: Record<string, string>;
        newHeaders: string[];
        newRows: any[][];
        units: (string | null)[];
    }) {
        const {
            originalHeaders,
            originalHeadersClean,
            originalTypes,
            apiSchema,
            apiComments,
            newHeaders,
            newRows,
            units,
        } = params;

        const clean = (arr: string[]) => arr.map(h => this.cleanHeaderKey(h));
        const newHeadersClean = clean(newHeaders);

        const useApiSchema = Object.keys(apiSchema).length > 0;
        const expectedTypes = this.resolveExpectedColumnTypes(
            originalHeaders,
            originalHeadersClean,
            originalTypes,
            apiSchema
        );

        // Order-insensitive: accept any column ordering as long as the same
        // (normalized) column names are present.
        const columnsOk = this.sameColumnSet(
            newHeadersClean,
            originalHeadersClean
        );
        const columnsMsg = columnsOk
            ? 'OK'
            : this.buildColumnMismatchMessage(
                  originalHeadersClean,
                  newHeaders,
                  newHeadersClean
              );

        // Realign incoming rows to the original column order so every positional
        // check below (and the eventual upload) maps each value to the right column.
        const alignedRows = columnsOk
            ? this.reorderRowsToOriginal(
                  newHeadersClean,
                  originalHeadersClean,
                  newRows
              )
            : newRows;

        // Value/format checks are only meaningful once the columns line up. Running them
        // on mismatched columns yields misleading errors (e.g. an item name checked against
        // a DATE column), so skip them until the columns match.
        const valueCheck = columnsOk
            ? this.validateRowValuesAgainstTypes(
                  originalHeaders,
                  expectedTypes,
                  alignedRows
              )
            : { ok: true, typesMsg: 'OK' };
        let typesOk = valueCheck.ok;
        let typesMsg = valueCheck.typesMsg;

        // Date columns: accept any separator/ordering and rewrite each value to the table's
        // declared format before upload, so e.g. 2024-01-02, 2024/01/02, 02/01/2024 and
        // 02-01-2024 are all treated as the same date and stored consistently.
        let finalRows = alignedRows;
        if (columnsOk && typesOk) {
            const useComments = Object.keys(apiComments).length > 0;
            const dateFormatByColumn = originalHeaders.map((origHeader, i) => {
                const origHeaderClean = originalHeadersClean[i];
                const isDateColumn = useApiSchema
                    ? (
                          apiSchema[origHeaderClean] || apiSchema[origHeader]
                      )?.toUpperCase() === 'DATE'
                    : originalTypes[i] === 'DATE';
                if (!isDateColumn) return null;
                const fmt = useComments
                    ? apiComments[origHeaderClean] || apiComments[origHeader]
                    : units[i];
                if (!fmt || fmt === 'No Unit' || fmt === 'default') return null;
                return fmt;
            });

            if (dateFormatByColumn.some(f => !!f)) {
                const result = this.normalizeDateColumnsForAppend(
                    alignedRows,
                    dateFormatByColumn,
                    originalHeaders
                );
                if (result.error) {
                    typesOk = false;
                    typesMsg = result.error;
                } else {
                    finalRows = result.rows;
                }
            }
        }

        return {
            ok: columnsOk && typesOk,
            columnsOk,
            typesOk,
            columnsMsg,
            typesMsg,
            alignedRows: finalRows,
        };
    }
}
